<template>
  <v-container class="payment-details py-7">
    <v-row>
      <v-col cols="12">
        <div class="title-container">
          <div class="title-block">
            <span class="page-title">Análise do comprovante</span>
            <div class="payment-details__subtitle">Confira os dados enviados pela devedora e registre a análise.</div>
            <v-breadcrumbs class="app-breadcrumb" divider="›" :items="breadcrumbs"></v-breadcrumbs>
          </div>
          <div class="button-group">
            <v-btn elevation="0" color="primary" outlined to="/comprovantes-pagamento">Voltar</v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <v-row v-if="data.has_inconsistency">
      <v-col cols="12">
        <v-alert type="warning" outlined prominent>
          {{ data.inconsistency_reason || "Comprovante marcado com inconsistência." }}
        </v-alert>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" md="7">
        <v-card class="card-form-outlined">
          <v-card-title class="card-group-title">Dados do envio</v-card-title>
          <v-card-text>
            <v-row>
              <v-col cols="12" sm="6"><strong>CPF/CNPJ:</strong> {{ data.creditor_registration }}</v-col>
              <v-col cols="12" sm="6"><strong>Processo:</strong> {{ data.process_number }}</v-col>
              <v-col cols="12" sm="6"><strong>Valor:</strong> {{ formatCurrency(data.paid_amount) }}</v-col>
              <v-col cols="12" sm="6"><strong>Data do pagamento:</strong> {{ data.payment_date | defaultDate("DD/MM/YYYY") }}</v-col>
              <v-col cols="12" sm="6"><strong>Responsável:</strong> {{ data.payment_responsible }}</v-col>
              <v-col cols="12" sm="6"><strong>Tipo de pagador:</strong> {{ data.payer_type | enum(dataset.payerTypes, "id", "name") }}</v-col>
              <v-col cols="12" sm="6"><strong>CPF/CNPJ pagador:</strong> {{ data.payer_registration || "-" }}</v-col>
              <v-col cols="12" sm="6">
                <strong>Pagamento por terceiro:</strong>
                <v-chip v-if="data.is_third_party_payment" x-small color="warning" dark>Sim</v-chip>
                <span v-else>Não</span>
              </v-col>
              <v-col cols="12" sm="6"><strong>Tipo:</strong> {{ data.document_type | enum(dataset.documentTypes, "id", "name") }}</v-col>
              <v-col cols="12" sm="6"><strong>Enviado em:</strong> {{ data.submitted_at | defaultDate("DD/MM/YYYY HH:mm") }}</v-col>
              <v-col cols="12" sm="6">
                <strong>Origem:</strong>
                {{ submissionOrigin(data) }}
              </v-col>
              <v-col cols="12" sm="6"><strong>Identificador:</strong> {{ data.external_reference || "-" }}</v-col>
              <v-col cols="12"><strong>Forma ou motivo:</strong> {{ data.payment_reason | enum(dataset.paymentReasons, "id", "name") }}</v-col>
              <v-col cols="12">
                <strong>Vínculo:</strong>
                <span v-if="data.linked_proceeding_id || data.linked_user_creditor_id">
                  Informações localizadas automaticamente na relação de credores.
                </span>
                <span v-else>Não foi possível localizar automaticamente.</span>
              </v-col>
            </v-row>
            <div v-if="data.documents && data.documents.length" class="mt-4">
              <strong>Documentos:</strong>
              <div>
                <v-btn v-for="(document, index) in data.documents" :key="document.id || index"
                  class="mt-2 mr-2" color="primary" outlined :href="document.document_url" target="_blank">
                  {{ document.document_name || `Documento ${index + 1}` }}
                </v-btn>
              </div>
            </div>
            <v-btn v-else-if="data.document_url" class="mt-4" color="primary" outlined :href="data.document_url" target="_blank">
              Visualizar documento
            </v-btn>
          </v-card-text>
        </v-card>
      </v-col>

      <v-col cols="12" md="5">
        <v-card class="card-form-outlined">
          <v-card-title class="card-group-title">Análise interna</v-card-title>
          <v-card-text>
            <v-form ref="form">
              <v-select dense outlined hide-details="auto" label="Status" :items="dataset.statuses"
                item-value="id" item-text="name" v-model="form.status"></v-select>
              <v-textarea class="mt-4" outlined hide-details="auto" label="Observações internas"
                v-model="form.internal_observation" rows="4"></v-textarea>
              <v-textarea class="mt-4" outlined hide-details="auto" label="Inconsistência"
                v-model="form.inconsistency_reason" rows="3"></v-textarea>
              <v-btn class="mt-4" color="primary" elevation="0" :loading="saving" @click="save">Salvar análise</v-btn>
            </v-form>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from "axios";
import paymentProofStatuses from "@/common/data/payment-proof-status.data";
import paymentProofDocumentTypes from "@/common/data/payment-proof-document-types.data";

export default {
  name: "PaymentProofDetails",
  data: () => ({
    breadcrumbs: [
      { text: "Homepage", disabled: false, to: "/home", exact: true },
      { text: "Comprovantes de pagamento", disabled: false, to: "/comprovantes-pagamento", exact: true },
      { text: "Detalhes", disabled: true, exact: true },
    ],
    dataset: {
      statuses: paymentProofStatuses,
      documentTypes: paymentProofDocumentTypes,
      payerTypes: [
        { id: "creditor", name: "Próprio credor" },
        { id: "recovering_company", name: "Recuperanda" },
        { id: "partner", name: "Sócio" },
        { id: "other_company", name: "Outra empresa" },
        { id: "other", name: "Outro terceiro" },
      ],
      paymentReasons: [
        { id: "pix", name: "Pix" },
        { id: "deposit", name: "Depósito em conta" },
        { id: "judicial_block", name: "Bloqueio judicial" },
        { id: "court_permit", name: "Alvará judicial" },
        { id: "agreement", name: "Acordo" },
        { id: "other", name: "Outro motivo" },
      ],
    },
    data: {},
    form: {},
    saving: false,
  }),
  created() {
    this.load();
  },
  methods: {
    async load() {
      try {
        const result = await axios.get(`/api/admin/payment-proofs/${this.$route.params.id}`);
        this.data = result.data.content;
        this.form = {
          status: this.data.status,
          internal_observation: this.data.internal_observation,
          inconsistency_reason: this.data.inconsistency_reason,
        };
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      }
    },
    async save() {
      this.saving = true;
      try {
        await axios.patch(`/api/admin/payment-proofs/${this.$route.params.id}/status`, {
          status: this.form.status,
          internal_observation: this.form.internal_observation,
        });
        await axios.post(`/api/admin/payment-proofs/${this.$route.params.id}/observations`, {
          internal_observation: this.form.internal_observation,
          inconsistency_reason: this.form.inconsistency_reason,
        });
        this.$dialog.notify.info("Análise salva com sucesso", { position: "top-right", timeout: 2000 });
        await this.load();
      } catch (error) {
        this.$dialog.notify.error(this.$codes(error), { position: "top-right", timeout: 2000 });
      } finally {
        this.saving = false;
      }
    },
    formatCurrency(value) {
      return Number(value || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
    },
    submissionOrigin(item) {
      if (!item.submitted_by_client_user_id) return "Inserido pela Administração Judicial";
      if (item.payment_proof_import_id) return `Inserido pela devedora · lote #${item.payment_proof_import_id}`;
      return "Inserido pela devedora · envio individual";
    },
  },
};
</script>

<style scoped>
.payment-details {
  max-width: 1440px;
}

.payment-details__subtitle {
  margin-top: 6px;
  color: #667085;
  font-size: 14px;
}

.payment-details .card-form-outlined {
  border-color: #e4e7ec;
  border-radius: 10px;
}
</style>
